import { useAccountingCompany } from '../components/HoldingAccounting';
import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

export default function FinancialReports() {
  const companyId = useAccountingCompany();
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  const base = `${API}/companies/${companyId}/finance`;

  const [tab, setTab] = useState('company');
  const [companyReport, setCompanyReport] = useState(null);
  const [projectReport, setProjectReport] = useState([]);
  const [recvAging, setRecvAging] = useState(null);
  const [payAging, setPayAging] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const initialLoading = useInitialLoading(loading);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchReport = async (type) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (dateFrom) qs.set('date_from', dateFrom);
      if (dateTo) qs.set('date_to', dateTo);

      if (type === 'company') {
        const res = await fetch(`${base}/reports/company?${qs}`, { headers: getAuthHeaders() });
        if (res.ok) setCompanyReport(await res.json());
      } else if (type === 'projects') {
        const res = await fetch(`${base}/reports/projects?${qs}`, { headers: getAuthHeaders() });
        if (res.ok) { const d = await res.json(); setProjectReport(Array.isArray(d) ? d : []); }
      } else if (type === 'recv-aging') {
        const res = await fetch(`${base}/reports/receivables-aging`, { headers: getAuthHeaders() });
        if (res.ok) setRecvAging(await res.json());
      } else if (type === 'pay-aging') {
        const res = await fetch(`${base}/reports/payables-aging`, { headers: getAuthHeaders() });
        if (res.ok) setPayAging(await res.json());
      } else if (type === 'audit') {
        const res = await fetch(`${base}/reports/audit?${qs}`, { headers: getAuthHeaders() });
        if (res.ok) { const d = await res.json(); setAuditLogs(d.data ? d.data : Array.isArray(d) ? d : []); }
      }
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { if (companyId) fetchReport(tab); }, [companyId, tab]);

  const fmt = (v) => '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
  const sectionStyle = { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 20 };
  const btnStyle = { padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 };
  const inputStyle = { padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.85rem' };
  const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', fontSize: '0.8rem', color: '#64748b', fontWeight: 600 };
  const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };

  const tabBtns = [
    { key: 'company', label: '🏢 Company', color: '#3b82f6' },
    { key: 'projects', label: '📁 Projects', color: '#8b5cf6' },
    { key: 'recv-aging', label: '📥 Recv Aging', color: '#f59e0b' },
    { key: 'pay-aging', label: '📤 Pay Aging', color: '#f97316' },
    { key: 'audit', label: '📋 Audit Trail', color: '#6b7280' },
  ];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 24px', fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>📊 Reports & History</h2>

      {/* Tab Toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {tabBtns.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{ ...btnStyle, background: tab === t.key ? t.color : '#f1f5f9', color: tab === t.key ? '#fff' : '#374151' }}>{t.label}</button>
        ))}
      </div>

      {/* Date Filter */}
      {(tab === 'company' || tab === 'projects' || tab === 'audit') && (
        <div style={{ ...sectionStyle, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.85rem', color: '#64748b' }}>From:</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
          <label style={{ fontSize: '0.85rem', color: '#64748b' }}>To:</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
          <button onClick={() => fetchReport(tab)} style={{ ...btnStyle, background: '#3b82f6', color: '#fff' }}>📊 Generate</button>
        </div>
      )}

      {initialLoading && <div style={{ textAlign: 'center', padding: 40 }}>Loading report...</div>}

      {/* Company Report */}
      {tab === 'company' && companyReport && !loading && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
            <div style={{ ...sectionStyle, borderLeft: '4px solid #22c55e', marginBottom: 0 }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Expected Revenue</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#22c55e' }}>{fmt(companyReport.expected_revenue)}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 4 }}>Discounts: {fmt(companyReport.total_discounts_recv)} • Tax: {fmt(companyReport.total_tax_recv)}</div>
            </div>
            <div style={{ ...sectionStyle, borderLeft: '4px solid #3b82f6', marginBottom: 0 }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Collected Revenue</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#3b82f6' }}>{fmt(companyReport.collected_revenue)}</div>
              <div style={{ fontSize: '0.75rem', color: '#f59e0b', marginTop: 4 }}>Outstanding: {fmt(companyReport.outstanding_receivables)}</div>
            </div>
            <div style={{ ...sectionStyle, borderLeft: '4px solid #ef4444', marginBottom: 0 }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Overdue Receivables</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#ef4444' }}>{fmt(companyReport.overdue_receivables)}</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
            <div style={{ ...sectionStyle, borderLeft: '4px solid #ef4444', marginBottom: 0 }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Expected Expenses</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#ef4444' }}>{fmt(companyReport.expected_expenses)}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 4 }}>Discounts: {fmt(companyReport.total_discounts_pay)} • Tax: {fmt(companyReport.total_tax_pay)}</div>
            </div>
            <div style={{ ...sectionStyle, borderLeft: '4px solid #f97316', marginBottom: 0 }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Expenses Paid</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#f97316' }}>{fmt(companyReport.expenses_paid)}</div>
              <div style={{ fontSize: '0.75rem', color: '#f97316', marginTop: 4 }}>Outstanding: {fmt(companyReport.outstanding_payables)}</div>
            </div>
            <div style={{ ...sectionStyle, borderLeft: '4px solid #ef4444', marginBottom: 0 }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Overdue Payables</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#ef4444' }}>{fmt(companyReport.overdue_payables)}</div>
            </div>
          </div>
          <div style={{ ...sectionStyle, borderLeft: '4px solid #8b5cf6', textAlign: 'center' }}>
            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Net Cash</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: companyReport.net_cash >= 0 ? '#22c55e' : '#ef4444' }}>{fmt(companyReport.net_cash)}</div>
          </div>
        </div>
      )}

      {/* Project Report */}
      {tab === 'projects' && !loading && (
        <div style={sectionStyle}>
          {projectReport.length === 0 ? <div style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>No project data</div> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Project</th><th style={thStyle}>Category</th><th style={thStyle}>Status</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Expected Rev.</th><th style={{ ...thStyle, textAlign: 'right' }}>Collected</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Outstanding Recv.</th><th style={{ ...thStyle, textAlign: 'right' }}>Expenses Paid</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Outstanding Pay.</th><th style={{ ...thStyle, textAlign: 'right' }}>Net Cash</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Collection %</th>
                  </tr>
                </thead>
                <tbody>
                  {projectReport.map(p => (
                    <tr key={p.id}>
                      <td style={{ ...tdStyle, fontWeight: 500 }}>{p.name}</td>
                      <td style={tdStyle}>{p.category || '-'}</td>
                      <td style={tdStyle}>{p.status}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(p.expected_revenue)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#22c55e' }}>{fmt(p.collected_revenue)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#f59e0b' }}>{fmt(p.outstanding_receivables)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#ef4444' }}>{fmt(p.expenses_paid)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#f97316' }}>{fmt(p.outstanding_payables)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: p.net_cash >= 0 ? '#22c55e' : '#ef4444' }}>{fmt(p.net_cash)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                          <div style={{ width: 60, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${Math.min(p.collection_pct, 100)}%`, height: '100%', background: p.collection_pct >= 80 ? '#22c55e' : p.collection_pct >= 50 ? '#f59e0b' : '#ef4444', borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: '0.8rem' }}>{p.collection_pct}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Receivables Aging */}
      {tab === 'recv-aging' && recvAging && !loading && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
            {[
              { key: '1_7', label: '1-7 Days', color: '#f59e0b' },
              { key: '8_30', label: '8-30 Days', color: '#f97316' },
              { key: '31_60', label: '31-60 Days', color: '#ef4444' },
              { key: '61_90', label: '61-90 Days', color: '#dc2626' },
              { key: '90_plus', label: '90+ Days', color: '#991b1b' },
            ].map(b => (
              <div key={b.key} style={{ ...sectionStyle, borderTop: `3px solid ${b.color}`, textAlign: 'center', marginBottom: 0 }}>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{b.label}</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: b.color }}>{fmt(recvAging.totals?.[b.key])}</div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{recvAging.aging?.[b.key]?.length || 0} items</div>
              </div>
            ))}
          </div>

          {Object.entries(recvAging.aging || {}).map(([bucket, items]) => (
            items.length > 0 && (
              <div key={bucket} style={sectionStyle}>
                <h4 style={{ margin: '0 0 12px', fontSize: '0.9rem', fontWeight: 600 }}>{bucket.replace('_', '-').replace('plus', '+')} Days Overdue</h4>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={thStyle}>Ref</th><th style={thStyle}>Payer</th><th style={thStyle}>Project</th><th style={{ ...thStyle, textAlign: 'right' }}>Amount</th><th style={thStyle}>Days Overdue</th></tr></thead>
                    <tbody>
                      {items.map(r => (
                        <tr key={r.id}><td style={tdStyle}>{r.reference}</td><td style={tdStyle}>{r.payer}</td><td style={tdStyle}>{r.project}</td><td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{fmt(r.amount)}</td><td style={{ ...tdStyle, color: '#ef4444', fontWeight: 600 }}>{r.days_overdue}d</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          ))}
        </div>
      )}

      {/* Payables Aging */}
      {tab === 'pay-aging' && payAging && !loading && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
            {[
              { key: '1_7', label: '1-7 Days', color: '#f59e0b' },
              { key: '8_30', label: '8-30 Days', color: '#f97316' },
              { key: '31_60', label: '31-60 Days', color: '#ef4444' },
              { key: '61_90', label: '61-90 Days', color: '#dc2626' },
              { key: '90_plus', label: '90+ Days', color: '#991b1b' },
            ].map(b => (
              <div key={b.key} style={{ ...sectionStyle, borderTop: `3px solid ${b.color}`, textAlign: 'center', marginBottom: 0 }}>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{b.label}</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: b.color }}>{fmt(payAging.totals?.[b.key])}</div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{payAging.aging?.[b.key]?.length || 0} items</div>
              </div>
            ))}
          </div>

          {Object.entries(payAging.aging || {}).map(([bucket, items]) => (
            items.length > 0 && (
              <div key={bucket} style={sectionStyle}>
                <h4 style={{ margin: '0 0 12px', fontSize: '0.9rem', fontWeight: 600 }}>{bucket.replace('_', '-').replace('plus', '+')} Days Overdue</h4>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={thStyle}>Ref</th><th style={thStyle}>Payee</th><th style={thStyle}>Project</th><th style={{ ...thStyle, textAlign: 'right' }}>Amount</th><th style={thStyle}>Days Overdue</th></tr></thead>
                    <tbody>
                      {items.map(p => (
                        <tr key={p.id}><td style={tdStyle}>{p.reference}</td><td style={tdStyle}>{p.payee}</td><td style={tdStyle}>{p.project}</td><td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{fmt(p.amount)}</td><td style={{ ...tdStyle, color: '#ef4444', fontWeight: 600 }}>{p.days_overdue}d</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          ))}
        </div>
      )}

      {/* Audit Trail */}
      {tab === 'audit' && !loading && (
        <div style={sectionStyle}>
          {auditLogs.length === 0 ? <div style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>No audit logs found</div> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={thStyle}>Timestamp</th><th style={thStyle}>User</th><th style={thStyle}>Action</th><th style={thStyle}>Type</th><th style={thStyle}>Description</th></tr></thead>
                <tbody>
                  {auditLogs.map(log => (
                    <tr key={log.id}>
                      <td style={tdStyle}>{log.created_at ? new Date(log.created_at).toLocaleString() : '-'}</td>
                      <td style={tdStyle}>{log.user?.name || '-'}</td>
                      <td style={tdStyle}><span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: '0.75rem', background: '#f1f5f9', fontWeight: 600 }}>{log.action}</span></td>
                      <td style={tdStyle}>{log.auditable_type ? log.auditable_type.split('\\').pop() : '-'}</td>
                      <td style={tdStyle}>{log.description || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
